import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  FileText, Plus, Send, CheckCircle, Clock, 
  AlertCircle, X, Download, Euro, Users, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    customer_email: '',
    customer_name: '',
    customer_tax_id: '',
    customer_address: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    due_days: 30,
    auto_send: true
  });

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/leads/invoices${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const iva = subtotal * 0.21;
    return { subtotal, iva, total: subtotal + iva };
  };

  const handleCreateInvoice = async () => {
    if (!formData.customer_email || !formData.customer_name || formData.items.some(i => !i.description)) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/leads/invoices/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Factura ${res.data.invoice_number} creada`);
      setShowCreate(false);
      setFormData({
        customer_email: '',
        customer_name: '',
        customer_tax_id: '',
        customer_address: '',
        items: [{ description: '', quantity: 1, unit_price: 0 }],
        notes: '',
        due_days: 30,
        auto_send: true
      });
      fetchInvoices();
    } catch (error) {
      toast.error('Error al crear la factura');
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/leads/invoices/${invoiceId}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Factura marcada como pagada');
      fetchInvoices();
    } catch (error) {
      toast.error('Error al actualizar la factura');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Pendiente' },
      paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Pagada' },
      overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Vencida' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const totals = calculateTotal();

  // Calculate summary stats
  const stats = {
    total: invoices.length,
    pending: invoices.filter(i => i.status === 'pending').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Facturas</h2>
          <p className="text-slate-600">Gestión de facturación a clientes</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Factura
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
              <div className="text-sm text-slate-500">Total facturas</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{stats.pending}</div>
              <div className="text-sm text-slate-500">Pendientes</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{stats.paid}</div>
              <div className="text-sm text-slate-500">Pagadas</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Euro className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{stats.paidAmount.toFixed(0)}€</div>
              <div className="text-sm text-slate-500">Cobrado</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'paid'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === f 
                ? 'bg-indigo-100 text-indigo-700' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f === 'all' ? 'Todas' : f === 'pending' ? 'Pendientes' : 'Pagadas'}
          </button>
        ))}
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Nº Factura</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Vencimiento</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Estado</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                  Cargando facturas...
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay facturas</p>
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <span className="font-mono font-medium text-slate-800">{invoice.invoice_number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-800">{invoice.customer_name}</div>
                    <div className="text-sm text-slate-500">{invoice.customer_email}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {invoice.created_at?.slice(0, 10)}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {invoice.due_date?.slice(0, 10)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="font-semibold text-slate-800">{invoice.total?.toFixed(2)}€</span>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {invoice.status === 'pending' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => markAsPaid(invoice.invoice_id)}
                        className="gap-1"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Marcar pagada
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Invoice Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-slate-800">Nueva Factura</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre del cliente *
                  </label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Academia XYZ"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="admin@academia.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    NIF/CIF
                  </label>
                  <input
                    type="text"
                    value={formData.customer_tax_id}
                    onChange={(e) => setFormData({ ...formData, customer_tax_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="B12345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Días para vencimiento
                  </label>
                  <input
                    type="number"
                    value={formData.due_days}
                    onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={formData.customer_address}
                    onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Calle Principal 123, 28001 Madrid"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-700">Conceptos</label>
                  <Button size="sm" variant="outline" onClick={addItem} className="gap-1">
                    <Plus className="w-3 h-3" />
                    Añadir
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg"
                        placeholder="Descripción del servicio"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-center"
                        min="1"
                      />
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="w-28 px-3 py-2 border border-slate-200 rounded-lg text-right"
                        step="0.01"
                        placeholder="0.00€"
                      />
                      <button 
                        onClick={() => removeItem(index)}
                        className="p-2 text-slate-400 hover:text-red-500"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">{totals.subtotal.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">IVA (21%):</span>
                  <span className="font-medium">{totals.iva.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                  <span>Total:</span>
                  <span className="text-indigo-600">{totals.total.toFixed(2)}€</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notas (opcional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  placeholder="Información adicional..."
                />
              </div>

              {/* Auto send */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_send}
                  onChange={(e) => setFormData({ ...formData, auto_send: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                />
                <span className="text-sm text-slate-600">Enviar factura por email automáticamente</span>
              </label>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateInvoice} className="gap-2">
                <FileText className="w-4 h-4" />
                Crear Factura
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManager;
